import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCommonSelector } from '@wajeez-web/stores/common';
import {
  useCreatePolicyMultilingual,
  useEditPolicyMultilingual,
  useEffectCancelled,
  useGetPolicyByIdMultilingual,
} from '@wajeez-web/components/helpers';
import { WajeezPageContainer } from '@wajeez-web/components/layouts';
import { WajeezMultilingualFormBuilder } from '@wajeez-web/components/form-builder';
import { Content, Server } from '@wajeez-web/core/api-base';
import {
  clearPolicyByIdMultilingual,
  createPolicyMultilingual,
  editPolicyMultilingual,
  getPolicyByIdMultilingual,
} from '@wajeez-web/stores/product';
import { Spin } from '@wajeez-web/components/ui';
import { Languages } from '@wajeez-web/core/utils';

const ModifyPolicyPage = () => {
  const { id } = useParams<{ id?: string }>();

  const navigate = useNavigate();

  const { lang } = useCommonSelector((state) => state.common.settings);

  const policyById = useGetPolicyByIdMultilingual();
  const createPolicyMultilingualState = useCreatePolicyMultilingual();
  const editPolicyMultilingualState = useEditPolicyMultilingual();

  const { t } = useTranslation();

  useEffectCancelled(
    (canceller: AbortController) => {
      if (id) {
        getPolicyByIdMultilingual(parseInt(id));
      }

      return () => {
        canceller.abort();

        clearPolicyByIdMultilingual();
      };
    },
    [id]
  );

  const saveData = async (values: Server.CreatePolicyV2Input) => {
    if (id) {
      editPolicyMultilingual(
        {
          ...values,
          id: parseInt(id),
        },
        () => {
          navigate(-1);
        }
      );
    } else {
      createPolicyMultilingual(
        {
          ...values,
        },
        () => {
          navigate(-1);
        }
      );
    }
  };

  return (
    <div>
      <WajeezPageContainer
        title={t(`general.${`${id ? 'Edit' : 'Create New'} Policy`}`, '')}
        desc={
          lang === 'en'
            ? 'This information will be displayed publicly so be careful what you share.'
            : 'هذه المعلومات ستنشر إلى العلن, لذلك كن حذرا مما تنشر.'
        }
      >
        <div className="sm:py-8 sm:px-4">
          {policyById.status === 'loading' ? (
            <div className="h-[500px] flex justify-center items-center">
              <Spin />
            </div>
          ) : (
            <WajeezMultilingualFormBuilder<Server.CreatePolicyV2Input>
              isUpdate={
                createPolicyMultilingualState.status === 'loading' ||
                editPolicyMultilingualState.status === 'loading'
              }
              hasToggleButton={true}
              onSubmit={(props) => {
                saveData(props);
              }}
              formValues={
                policyById.data
                  ? policyById.data
                  : { profiles: [{ languageId: Languages.AR }] }
              }
              rows={{
                policyType: {
                  disabled: id !== null,
                  order: 300,
                  label: 'Policy Type',
                  placeHolder: 'Select policy type',
                  required: false,
                  fieldType: Content.FieldType.Select,
                  fieldOptions: {
                    selectOptions: [
                      { value: 0, label: 'Privacy Policy ' },
                      { value: 1, label: 'Terms Of Service ' },
                      { value: 2, label: 'About' },
                    ],
                  },
                },
              }}
              profileRows={{
                title: {
                  order: 100,
                  label: 'Title',
                  placeHolder: 'Enter policy title',
                  required: true,
                  fieldType: Content.FieldType.Text,
                  fieldOptions: {
                    maxLength: 100,
                    minLength: 4,
                  },
                },
                body: {
                  order: 200,
                  label: 'Body',
                  placeHolder: 'Enter policy body',
                  required: true,
                  fieldType: Content.FieldType.TineMCE,
                  fieldOptions: {
                    minLength: 4,
                  },
                },
              }}
            />
          )}
        </div>
      </WajeezPageContainer>
    </div>
  );
};

export default ModifyPolicyPage;
